import React, { useEffect, useRef, useState } from "react";
import { getApi } from "../../../Services/apiService";
import CircularProgress from "@mui/material/CircularProgress";
import { calculateAge } from "../../../Utils/family-tree/dateAndTime";
import { renderNameWithColor } from "../../../Utils/colors";
import {
  createIndividualChat,
  createGroupChat,
  updateGroupChat,
  getMessagingErrorMessage,
  setChatAsAdminSupport,
  sendMessage,
} from "../../../Services/messagingService";
import { toast } from "react-toastify";
import {
  getFamilyTreeIdFromStorage,
  resolveAdminDetailsForSupport,
} from "../../../Utils/headOfLineRole";
import { formatDashboardFamilyLineHeadName } from "../../../Utils/dashboardMemberDisplayName";
import {
  FilterTriggerLabel,
  filterTriggerClass,
  formatFilterValueList,
} from "../../../Utils/filterTriggerLabel";

/** Normalize API member gender: 1 male, 2 female, 3 binary, 4 other (see sortedGenderOptions). */
function getMemberGenderCode(member) {
  const g = member?.gender ?? member?.sex;
  const n = Number(g);
  if (n === 1 || n === 2 || n === 3 || n === 4) return String(n);
  if (g === "1" || g === "2" || g === "3" || g === "4") return g;
  if (typeof g === "string") {
    const t = g.trim().toLowerCase();
    if (t === "male" || t === "m") return "1";
    if (t === "female" || t === "f") return "2";
    if (t === "binary") return "3";
    if (t === "other") return "4";
  }
  return null;
}

/** Auto message sent to the family admin from the mobile "Verify Your Email" prompt. */
const VERIFY_EMAIL_REQUEST_TEXT = "Please Verify My Email";

/** Family admin can't raise a support request with themselves (same check as ChatList). */
function isLoggedInUserFamilyAdmin() {
  try {
    const role = JSON.parse(localStorage.getItem("loggedIn_user_role") || "{}");
    return role?.is_admin === true;
  } catch {
    return false;
  }
}

/** Per-user "Already Verified" acknowledgement – hides the prompt for good. */
const getVerifyEmailAckKey = (userId) => `verify_email_prompt_ack_${userId}`;

function hasAcknowledgedVerifyEmail(userId) {
  if (!userId) return false;
  try {
    return localStorage.getItem(getVerifyEmailAckKey(userId)) === "true";
  } catch {
    return false;
  }
}

const ChatModal = ({ onClose, chatMode, family_tree_id, editGroupChat }) => {
  const [search, setSearch] = useState("");
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Age range filter (matches Create Event "Age Range" dropdown)
  const [showAgeRange, setShowAgeRange] = useState(false);
  const [ageFrom, setAgeFrom] = useState("");
  // Range the list is currently narrowed to (inputs are drafts until Apply)
  const [appliedAgeRange, setAppliedAgeRange] = useState(null);
  const [ageTo, setAgeTo] = useState("");

  // Family line / color filter (matches Create Event "Head of Line/Color")
  const [showFamilyColor, setShowFamilyColor] = useState(false);
  const [familyColorList, setFamilyColorList] = useState([]);
  const [selectedFamilyColor, setSelectedFamilyColor] = useState([]); // Temporary selection in dropdown
  const [appliedFamilyColor, setAppliedFamilyColor] = useState([]); // Applied filter (used for filtering)

  // Gender filter (client-side; aligns with sortedGenderOptions: 1 male, 2 female, 3 binary, 4 other)
  const [showGender, setShowGender] = useState(false);
  const [selectedGender, setSelectedGender] = useState(""); // '' | '1' | '2' | 'other' ('other' = genders 3–4: Binary & Other per member forms)
  const [appliedGender, setAppliedGender] = useState(""); // Applied filter

  // Member selection states
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [creatingChat, setCreatingChat] = useState(false);
  const [creatingChatForUserId, setCreatingChatForUserId] = useState(null); // Track which user's chat is being created
  const [currentUserId, setCurrentUserId] = useState(null);

  // Sorting State
  const [sortBy, setSortBy] = useState("name"); // default: A–Z by name
  const [sortOrder, setSortOrder] = useState("asc"); // 'asc' or 'desc'

  // Mobile: Email column is hidden and replaced by the "Verify Your Email" prompt
  const [isMobileView, setIsMobileView] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 768px)").matches
  );
  const [showVerifyEmail, setShowVerifyEmail] = useState(false);
  // Email column is for family admins only, and never on mobile
  const [isFamilyAdmin] = useState(isLoggedInUserFamilyAdmin);
  const showEmailColumn = !isMobileView && isFamilyAdmin;
  const [sendingVerifyRequest, setSendingVerifyRequest] = useState(false);
  const verifyPromptShownRef = useRef(false);

  // Refs for dropdowns (for click‑outside handling)
  const ageRangeRef = useRef(null);
  const genderRef = useRef(null);
  const famRef = useRef(null);

  // Helper function to format person name with suffix or special name (matching CreateEventModal)
  const formatPersonName = (member) => {
    // Only use first_name, extract first word from name if first_name not available
    let firstName = member?.first_name || "";
    if (!firstName && member?.name) {
      // Extract first word from full name as fallback
      firstName = member.name.trim().split(' ')[0] || "Unnamed";
    }
    if (!firstName) {
      firstName = "Unnamed";
    }
    
    const suffix = member?.suffix || "";
    const specialName = member?.special_name || "";
    
    // Format name with suffix or special name
    if (suffix) {
      // Only add period for Sr or Jr (case-insensitive)
      const suffixUpper = suffix.trim().toUpperCase();
      const suffixWithPeriod = (suffixUpper === 'SR' || suffixUpper === 'JR') ? `${suffix.trim()}.` : suffix.trim();
      return `${firstName}, ${suffixWithPeriod}`;
    } else if (specialName) {
      return `${firstName} ${specialName}`;
    }
    return firstName;
  };

  const getHeadOfLineName = formatDashboardFamilyLineHeadName;

  // Helper: calculate age from member record (same logic as events)
  const getMemberAge = (member) => {
    if (member?.age !== undefined && member?.age !== null) {
      const age = parseInt(member.age, 10);
      if (!Number.isNaN(age) && age >= 0) return age;
    }

    let dob = member?.dob || member?.date_of_birth || "";
    if (!dob) return -1;

    if (
      typeof dob === "number" ||
      (typeof dob === "string" && /^-?\d+$/.test(dob))
    ) {
      const timestamp = parseInt(dob, 10);
      dob = new Date(timestamp).toISOString();
    }

    const ageStr = calculateAge(dob);
    if (!ageStr) return -1;
    return parseInt(ageStr, 10) || -1;
  };

  // Fetch members for chat (same endpoint as events)
  const fetchMembersForChat = async (filters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams(filters).toString();
      const endpoint = params
        ? `/family-members-for-invitation?family_tree_id=${family_tree_id}&${params}`
        : `/family-members-for-invitation?family_tree_id=${family_tree_id}`;

      const response = await getApi(endpoint);
      const data = response?.data;
      if (response?.statusCode === 200) {
        const list = data?.members || [];
        setMembers(list);
        // Filtered members will be set by useEffect that applies family color and search filters
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch available family line colors (for Head of Line/Color filter)
  const fetchFamilyColorData = async () => {
    try {
      const family_tree_id_value =
        getFamilyTreeIdFromStorage() || family_tree_id;
      const res = await getApi(
        `/family-members-count-by-filter?family_tree_id=${family_tree_id_value}&type=${0}`
      );

      setFamilyColorList(res?.data?.members || []);
    } catch (error) {
      console.error(error);
    }
  };

  // Get current user ID from localStorage
  useEffect(() => {
    try {
      const localUser = JSON.parse(localStorage.getItem("userDetail") || "{}");
      const userId = localUser?.user_id || localUser?.id || null;
      setCurrentUserId(userId);
    } catch (error) {
      console.error("Error getting current user ID:", error);
    }
  }, []);

  useEffect(() => {
    fetchMembersForChat();
  }, []);

  // Track mobile breakpoint (matches Messages page + picker media queries)
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsMobileView(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  // Mobile only: the Email column is hidden, so prompt once per open to get it verified.
  // Skipped for the family admin (would be a support request to themselves), when editing
  // an existing group (not a "start chat" flow), and once the user taps "Already Verified".
  useEffect(() => {
    if (!isMobileView || editGroupChat || verifyPromptShownRef.current) return;
    if (!currentUserId) return;
    if (isLoggedInUserFamilyAdmin()) return;
    if (hasAcknowledgedVerifyEmail(currentUserId)) return;
    verifyPromptShownRef.current = true;
    setShowVerifyEmail(true);
  }, [isMobileView, editGroupChat, currentUserId]);

  // When opening in edit mode, pre-fill group name and pre-select existing participants
  useEffect(() => {
    if (chatMode !== "group") return;
    if (editGroupChat) {
      setGroupName(editGroupChat.groupName || "");
      if (members.length > 0 && editGroupChat.participants && Array.isArray(editGroupChat.participants)) {
        const existingIds = members
          .filter((m) => editGroupChat.participants.includes(m.user_id))
          .map((m) => m.id);
        setSelectedMembers(existingIds);
      }
    } else {
      setGroupName("");
      setSelectedMembers([]);
    }
  }, [editGroupChat, chatMode, members.length]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ageRangeRef.current && !ageRangeRef.current.contains(e.target)) {
        setShowAgeRange(false);
      }
      if (famRef.current && !famRef.current.contains(e.target)) {
        setShowFamilyColor(false);
      }
      if (genderRef.current && !genderRef.current.contains(e.target)) {
        setShowGender(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle sorting
  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order if clicking the same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  /** After Age / Gender / Head of Line filters change, show A–Z by Name (matches user expectation for both chat modes). */
  const resetSortToNameAscending = () => {
    setSortBy("name");
    setSortOrder("asc");
  };

  // Apply filters (family color + search) and sorting
  // Note: Optional age range (from/to) is handled via API; minimum chat age is always enforced client-side.
  useEffect(() => {
    let filtered = [...members];

    // Only members aged 10+ (dob / date_of_birth / member.age — see getMemberAge)
    filtered = filtered.filter((member) => getMemberAge(member) >= 10);

    // Filter out the current user (can't chat with yourself)
    if (currentUserId) {
      filtered = filtered.filter((member) => member.user_id !== currentUserId);
    }

    // Family color filter (client-side, like CreateEventModal)
    if (appliedFamilyColor && appliedFamilyColor.length > 0) {
      filtered = filtered.filter((member) =>
        appliedFamilyColor.includes(member.hex_code)
      );
    }

    // Gender filter (client-side)
    if (appliedGender === "1" || appliedGender === "2") {
      filtered = filtered.filter(
        (member) => getMemberGenderCode(member) === appliedGender
      );
    } else if (appliedGender === "other") {
      filtered = filtered.filter((member) => {
        const c = getMemberGenderCode(member);
        return c === "3" || c === "4";
      });
    }

    // Search filter (client-side)
    if (search.trim()) {
      const term = search.toLowerCase();
      filtered = filtered.filter((member) => {
        const name = (member?.name || "").toLowerCase();
        const email = (
          member?.email ||
          member?.user?.email ||
          ""
        ).toLowerCase();
        return name.includes(term) || email.includes(term);
      });
    }

    const localeOpts = { sensitivity: "base", numeric: true };

    const compareByDisplayName = (a, b) => {
      const nameA = formatPersonName(a).toLowerCase();
      const nameB = formatPersonName(b).toLowerCase();
      let c = nameA.localeCompare(nameB, undefined, localeOpts);
      if (c === 0) {
        c = String(a?.id ?? "").localeCompare(String(b?.id ?? ""), undefined, {
          numeric: true,
        });
      }
      return c;
    };

    // Apply sorting
    if (sortBy) {
      filtered = [...filtered].sort((a, b) => {
        let comparison = 0;

        if (sortBy === "name") {
          comparison = compareByDisplayName(a, b);
        } else if (sortBy === "familyLine") {
          // Sort by head of line name first, then by color name, then by display name (stable A–Z within same line)
          const headA = getHeadOfLineName(a) || "Start/Top of Line";
          const headB = getHeadOfLineName(b) || "Start/Top of Line";
          const colorA = a?.color_name || "";
          const colorB = b?.color_name || "";

          comparison = headA.localeCompare(headB, undefined, localeOpts);
          if (comparison === 0) {
            comparison = colorA.localeCompare(colorB, undefined, localeOpts);
          }
          if (comparison === 0) {
            comparison = compareByDisplayName(a, b);
          }
        }

        return sortOrder === "asc" ? comparison : -comparison;
      });
    }

    setFilteredMembers(filtered);
  }, [members, appliedFamilyColor, appliedGender, search, sortBy, sortOrder, currentUserId]);

  // Age range handlers
  const handleAgeFromChange = (e) => {
    const value = e.target.value;
    setAgeFrom(value);
    if (ageTo && value && !Number.isNaN(parseInt(value, 10))) {
      const fromAge = parseInt(value, 10);
      const toAge = parseInt(ageTo, 10);
      if (!Number.isNaN(toAge) && fromAge >= toAge) {
        setAgeTo("");
      }
    }
  };

  const handleAgeToChange = (e) => {
    setAgeTo(e.target.value);
  };

  const handleApplyAgeRange = () => {
    if (!ageFrom || !ageTo) {
      return;
    }

    const fromAge = parseInt(ageFrom, 10);
    const toAge = parseInt(ageTo, 10);

    if (Number.isNaN(fromAge) || Number.isNaN(toAge)) {
      return;
    }

    if (toAge <= fromAge) {
      return;
    }

    // Define the filter object for the API query parameter (same as CreateEventModal)
    const filterParams = {
      ageFrom: fromAge,
      ageTo: toAge,
    };

    // Clear search input and family color filter (same as CreateEventModal)
    setSearch("");
    setAppliedFamilyColor([]);
    setSelectedFamilyColor([]);
    resetSortToNameAscending();

    // Make the API call with the filter (same as CreateEventModal)
    fetchMembersForChat(filterParams);
    setAppliedAgeRange({ from: fromAge, to: toAge });

    setShowAgeRange(false);
  };

  const handleClearAgeRange = () => {
    setAgeFrom("");
    setAgeTo("");
    setAppliedAgeRange(null);
    setSearch("");
    setAppliedFamilyColor([]);
    setSelectedFamilyColor([]);
    resetSortToNameAscending();
    // Reset to all members (same as CreateEventModal)
    fetchMembersForChat();
    setShowAgeRange(false);
  };

  // Family color handlers (same logic as CreateEventModal)
  const handleApplyFamilyColor = () => {
    if (!selectedFamilyColor || selectedFamilyColor.length === 0) {
      return;
    }

    // Clear any existing search input (same as CreateEventModal)
    setSearch("");

    // Apply the selected colors to the filter state
    // The useEffect will handle the actual filtering
    setAppliedFamilyColor([...selectedFamilyColor]);
    resetSortToNameAscending();
    setShowFamilyColor(false);
  };

  const handleClearFamilyColor = () => {
    setSelectedFamilyColor([]);
    setAppliedFamilyColor([]);
    setSearch("");
    resetSortToNameAscending();
    // Reset to all members (same as CreateEventModal) — drops the age range too
    setAgeFrom("");
    setAgeTo("");
    setAppliedAgeRange(null);
    fetchMembersForChat();
    setShowFamilyColor(false);
  };

  const handleApplyGender = () => {
    const next =
      selectedGender === "1" || selectedGender === "2" || selectedGender === "other"
        ? selectedGender
        : "";
    setAppliedGender(next);
    resetSortToNameAscending();
    setShowGender(false);
  };

  const handleClearGender = () => {
    setSelectedGender("");
    setAppliedGender("");
    resetSortToNameAscending();
    setShowGender(false);
  };

  // What each filter trigger reads while its filter is applied
  const ageRangeFilterValue = appliedAgeRange
    ? // word joiners keep the range on one line when the trigger wraps
      `${appliedAgeRange.from}\u2060–\u2060${appliedAgeRange.to}`
    : "";
  const familyColorFilterValue = (() => {
    if (!appliedFamilyColor || appliedFamilyColor.length === 0) return "";
    const names = appliedFamilyColor.map((hex) => {
      const entry = familyColorList.find((c) => c?.hex_code === hex);
      return entry?.color_name || entry?.name || "";
    });
    return (
      formatFilterValueList(names) || `${appliedFamilyColor.length} selected`
    );
  })();
  const genderFilterValue =
    appliedGender === "1"
      ? "Male"
      : appliedGender === "2"
      ? "Female"
      : appliedGender === "other"
      ? "Other"
      : "";

  //For the heading
  const getTitle = () => {
    if (chatMode === "individual") return "Start Individual Chat";
    if (editGroupChat) return "Add/Remove Members";
    return "Create Group Chat";
  };

  // Handle member selection via checkbox
  const handleMemberSelection = (memberId) => {
    setSelectedMembers((prev) => {
      if (chatMode === "individual") {
        // For individual chat, only one member can be selected
        return [memberId];
      } else {
        // For group chat, multiple members can be selected
        if (prev.includes(memberId)) {
          return prev.filter((id) => id !== memberId);
        } else {
          return [...prev, memberId];
        }
      }
    });
  };

  // Handle individual chat creation
  const handleCreateIndividualChat = async (member) => {
    if (!currentUserId) {
      toast.error("User not logged in. Please refresh the page.");
      return;
    }

    const otherUserId = member?.user_id;
    if (!otherUserId) {
      toast.error("Unable to get user information. Please try again.");
      return;
    }

    // Don't allow chatting with yourself
    if (currentUserId === otherUserId) {
      toast.error("You cannot start a chat with yourself.");
      return;
    }

    setCreatingChat(true);
    setCreatingChatForUserId(otherUserId); // Track which user's chat is being created
    try {
      const localUser = JSON.parse(localStorage.getItem("userDetail") || "{}");
      const familyTreeId =
        getFamilyTreeIdFromStorage() || family_tree_id;

      if (!familyTreeId) {
        toast.error("Unable to get family tree information. Please try again.");
        return;
      }

      // Get member info for the other participant from members list
      const otherMemberInfo = members.find(m => m.user_id === otherUserId);
      
      // Get current user's member info - first try from members list, then from localStorage
      let currentUserMember = members.find(m => m.user_id === currentUserId);
      
      // If current user not found in members list, get from localStorage and fetch full info
      if (!currentUserMember) {
        // Try to fetch current user's full member info from API
        try {
          const response = await getApi(
            `/family-members-for-invitation?family_tree_id=${familyTreeId}`
          );
          if (response?.statusCode === 200 && response?.data?.members) {
            currentUserMember = response.data.members.find(
              (m) => m.user_id === currentUserId
            );
          }
        } catch (error) {
          console.error("Error fetching current user's member info:", error);
        }
        
        // If still not found, construct from localStorage
        if (!currentUserMember && localUser) {
          currentUserMember = {
            user_id: localUser.user_id || localUser.id || currentUserId,
            first_name: localUser.name || localUser.first_name || "",
            name: localUser.name || "",
            email: localUser.email || "",
            // Note: color_name, hex_code, head_first_name might not be in localStorage
            // These will be updated later when the user opens the chat
            color_name: null,
            hex_code: "#000000",
            head_first_name: null,
            head_suffix: null,
            head_special_name: null,
          };
        }
      }
      
      const chatId = await createIndividualChat(
        currentUserId, 
        otherUserId,
        familyTreeId,
        currentUserMember || null,
        otherMemberInfo || null
      );
      toast.success("Chat created successfully!");
      
      // Close modal and pass chatId to parent if needed
      // You can modify onClose to accept chatId if you want to navigate to the chat
      onClose(chatId);
    } catch (error) {
      console.error("Error creating individual chat:", error);
      toast.error(getMessagingErrorMessage(error));
    } finally {
      setCreatingChat(false);
      setCreatingChatForUserId(null);
    }
  };

  // Handle group chat creation
  const handleCreateGroupChat = async () => {
    if (!currentUserId) {
      toast.error("User not logged in. Please refresh the page.");
      return;
    }

    if (selectedMembers.length === 0) {
      toast.error("Please select at least one member");
      return;
    }

    if (!groupName.trim()) {
      toast.error("Please enter a group name");
      return;
    }

    setCreatingChat(true);
    try {
      const localUser = JSON.parse(localStorage.getItem("userDetail") || "{}");
      const familyTreeId =
        getFamilyTreeIdFromStorage() || family_tree_id;

      if (!familyTreeId) {
        toast.error("Unable to get family tree information. Please try again.");
        setCreatingChat(false);
        return;
      }

      // Get user_ids for selected members
      const selectedUserIds = selectedMembers
        .map((memberId) => {
          const member = members.find((m) => m.id === memberId);
          return member?.user_id;
        })
        .filter((userId) => userId && userId !== currentUserId); // Filter out invalid and current user

      if (selectedUserIds.length === 0) {
        toast.error("Please select valid members");
        setCreatingChat(false);
        return;
      }

      // Get member info for all participants (including current user)
      const participantInfoMap = {};
      
      // Get current user's member info - first try from members list, then from localStorage
      let currentUserMember = members.find(m => m.user_id === currentUserId);
      
      // If current user not found in members list, get from localStorage and fetch full info
      if (!currentUserMember) {
        // Try to fetch current user's full member info from API
        try {
          const response = await getApi(
            `/family-members-for-invitation?family_tree_id=${familyTreeId}`
          );
          if (response?.statusCode === 200 && response?.data?.members) {
            currentUserMember = response.data.members.find(
              (m) => m.user_id === currentUserId
            );
          }
        } catch (error) {
          console.error("Error fetching current user's member info:", error);
        }
        
        // If still not found, construct from localStorage
        if (!currentUserMember && localUser) {
          currentUserMember = {
            user_id: localUser.user_id || localUser.id || currentUserId,
            first_name: localUser.name || localUser.first_name || "",
            name: localUser.name || "",
            email: localUser.email || "",
            color_name: null,
            hex_code: "#000000",
            head_first_name: null,
            head_suffix: null,
            head_special_name: null,
          };
        }
      }
      
      // Add current user to participantInfoMap
      if (currentUserMember) {
        participantInfoMap[currentUserMember.user_id || currentUserId] = currentUserMember;
      }
      
      // Get member info for selected participants
      selectedUserIds.forEach((userId) => {
        const member = members.find((m) => m.user_id === userId);
        if (member) {
          participantInfoMap[member.user_id || userId] = member;
        }
      });

      const chatId = await createGroupChat(
        currentUserId,
        groupName.trim(),
        selectedUserIds,
        familyTreeId,
        participantInfoMap
      );
      toast.success("Group chat created successfully!");
      
      // Close modal and pass chatId to parent if needed
      onClose(chatId);
    } catch (error) {
      console.error("Error creating group chat:", error);
      toast.error(
        getMessagingErrorMessage(error, "Failed to create group chat. Please try again.")
      );
    } finally {
      setCreatingChat(false);
    }
  };

  // Update existing group: set new participants (and optional group name)
  const handleUpdateGroupChat = async () => {
    if (!editGroupChat?.id || !currentUserId) {
      toast.error("Unable to update group. Please try again.");
      return;
    }

    const selectedUserIds = selectedMembers
      .map((memberId) => {
        const member = members.find((m) => m.id === memberId);
        return member?.user_id;
      })
      .filter(Boolean);

    const participants = [...new Set([currentUserId, ...selectedUserIds])].sort();
    if (participants.length < 2) {
      toast.error("Group must have at least 2 members.");
      return;
    }

    setCreatingChat(true);
    try {
      await updateGroupChat(editGroupChat.id, {
        participants,
        ...(groupName.trim() ? { groupName: groupName.trim() } : {}),
      });
      toast.success("Group updated successfully!");
      onClose(editGroupChat.id);
    } catch (error) {
      console.error("Error updating group chat:", error);
      toast.error(error?.message || "Failed to update group. Please try again.");
    } finally {
      setCreatingChat(false);
    }
  };

  // Check if a member is selected
  const isMemberSelected = (memberId) => {
    return selectedMembers.includes(memberId);
  };

  // "Verify Email": send the automatic request into the Admin Support chat,
  // which the admin sees under the Support Request tab.
  const handleSendVerifyEmailRequest = async () => {
    if (!currentUserId) {
      toast.error("User not logged in. Please refresh the page.");
      return;
    }

    setSendingVerifyRequest(true);
    try {
      const familyTreeId = getFamilyTreeIdFromStorage() || family_tree_id;
      if (!familyTreeId) {
        toast.error("Unable to get family tree information. Please try again.");
        return;
      }

      const adminDetails = await resolveAdminDetailsForSupport(familyTreeId);
      const adminId = adminDetails?.id;
      if (!adminId) {
        toast.error("Unable to reach the family admin. Please try again.");
        return;
      }

      const localUser = JSON.parse(localStorage.getItem("userDetail") || "{}");
      const currentUserMember =
        members.find((m) => m.user_id === currentUserId) || {
          user_id: currentUserId,
          first_name: localUser.name || localUser.first_name || "",
          name: localUser.name || "",
          email: localUser.email || "",
          color_name: null,
          hex_code: "#000000",
          head_first_name: null,
          head_suffix: null,
          head_special_name: null,
        };

      const adminName = adminDetails.name || "Admin";
      const adminInfo = {
        user_id: adminId,
        name: adminName,
        first_name: adminName.split(" ")[0] || "Admin",
      };

      const chatId = await createIndividualChat(
        currentUserId,
        adminId,
        familyTreeId,
        currentUserMember,
        adminInfo
      );
      await setChatAsAdminSupport(chatId);
      await sendMessage(
        chatId,
        currentUserId,
        VERIFY_EMAIL_REQUEST_TEXT,
        currentUserMember?.name || localUser?.name || null
      );

      toast.success("Request sent to Admin.");
      setShowVerifyEmail(false);
    } catch (error) {
      console.error("Error sending verify email request:", error);
      toast.error(
        getMessagingErrorMessage(error, "Failed to send request. Please try again.")
      );
    } finally {
      setSendingVerifyRequest(false);
    }
  };

  // "Already Verified": remember the choice for this user so the prompt never shows again
  const handleAlreadyVerified = () => {
    try {
      if (currentUserId) {
        localStorage.setItem(getVerifyEmailAckKey(currentUserId), "true");
      }
    } catch (error) {
      console.error("Error saving verify email acknowledgement:", error);
    }
    setShowVerifyEmail(false);
  };

  // Group: checkbox + Name + Family Line (+ Email); Individual: Name + Family Line (+ Email) + Action
  const memberTableColSpan = 3 + (showEmailColumn ? 1 : 0);

  return (
    <div style={{ position: "relative" }}>
      <div className="chat-popup-header-row" style={{ marginBottom: "12px" }}>
        <h2 className="chat-table-title" style={{ margin: 0 }}>{getTitle()}</h2>
        <button
          type="button"
          className="chat-popup-close"
          onClick={() => onClose(true)}
          aria-label="Close"
        >
          ×
        </button>
      </div>

      {/*Table view */}
      <div className="invite-grid chat-member-picker" style={{ width: "100%" }}>
        <div className="invite-right" style={{ width: "100%" }}>
          <div
            className={`invite-controls chat-invite-controls${
              chatMode === "group" ? " chat-invite-controls--group" : ""
            }`}
            style={{ display: "flex", gap: "10px", alignItems: "flex-end", marginBottom: "12px", flexWrap: "nowrap", width: "100%", boxSizing: "border-box" }}
          >
            {/*Group Name input - only for group chat, in same row as filters */}
            {chatMode && chatMode === "group" && (
              <div className="chat-group-name-field" style={{ display: "flex", flexDirection: "column", gap: "4px", width: "160px", flexShrink: 0 }}>
                <label htmlFor="groupName" style={{ fontSize: "12px", fontWeight: "500", color: "#333", whiteSpace: "nowrap", marginBottom: "0", lineHeight: "1.2" }}>
                  Add Group Name
                </label>
                <input
                  id="groupName"
                  type="text"
                  placeholder="Add Group Name"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "6px 10px",
                    border: "1px solid #D9D9D9",
                    borderRadius: "4px",
                    fontSize: "14px",
                    outline: "none",
                    height: "36px",
                    boxSizing: "border-box"
                  }}
                />
              </div>
            )}

            {/*Search field */}
            <div className="invite-search event-search" style={{ flex: "1", minWidth: "140px", maxWidth: "100%" }}>
              <input
                type="text"
                className="search-input"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ width: "100%", maxWidth: "100%" }}
              />
              <span className="invite-search-icon">
                <img src="" />
              </span>
            </div>

            {/* Age Range Dropdown */}
            <div
              ref={ageRangeRef}
              className="selctions-option position-relative"
              style={{ minWidth: "120px", width: "auto", flexShrink: 0 }}
            >
              <div
                className={filterTriggerClass(
                  "selection-drop text-nowrap",
                  Boolean(ageRangeFilterValue)
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAgeRange((prev) => !prev);
                }}
                style={{ width: "156px", minWidth: "140px" }}
              >
                <FilterTriggerLabel label="Age Range" value={ageRangeFilterValue} />
              </div>

              {showAgeRange && (
                <div
                  className="member-list"
                  style={{
                    width: "180px",
                    minWidth: "180px",
                    padding: "10px",
                    zIndex: 2000,
                    background: "#fff"
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "row", gap: "6px", alignItems: "flex-end" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "3px", flex: "1" }}>
                      <label style={{ fontSize: "11px", fontWeight: "500" }}>From</label>
                      <input
                        type="number"
                        value={ageFrom}
                        onChange={handleAgeFromChange}
                        placeholder="Age"
                        min="0"
                        style={{
                          padding: "4px 6px",
                          border: "1px solid #D9D9D9",
                          borderRadius: "4px",
                          fontSize: "12px",
                          width: "100%",
                          height: "28px",
                        }}
                      />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "3px", flex: "1" }}>
                      <label style={{ fontSize: "11px", fontWeight: "500" }}>To</label>
                      <input
                        type="number"
                        value={ageTo}
                        onChange={handleAgeToChange}
                        placeholder="Age"
                        min={ageFrom && !Number.isNaN(parseInt(ageFrom, 10)) ? parseInt(ageFrom, 10) + 1 : "0"}
                        style={{
                          padding: "4px 6px",
                          border: "1px solid #D9D9D9",
                          borderRadius: "4px",
                          fontSize: "12px",
                          width: "100%",
                          height: "28px",
                        }}
                      />
                    </div>
                  </div>

                  <div
                    className="btn-container"
                    style={{
                      display: "flex",
                      gap: "6px",
                      marginTop: "10px",
                      paddingTop: "10px",
                      borderTop: "1px solid #e0e0e0",
                    }}
                  >
                    <button
                      type="button"
                      className="btn btn-clear"
                      onClick={handleClearAgeRange}
                      style={{
                        flex: "1",
                        padding: "4px 8px",
                        fontSize: "12px",
                        borderRadius: "4px",
                        height: "28px",
                      }}
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      className="btn btn-apply"
                      onClick={handleApplyAgeRange}
                      style={{
                        flex: "1",
                        padding: "4px 8px",
                        fontSize: "12px",
                        borderRadius: "4px",
                        height: "28px",
                      }}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Gender dropdown */}
            <div
              ref={genderRef}
              className="selctions-option position-relative"
              style={{ minWidth: "120px", width: "auto", flexShrink: 0 }}
            >
              <div
                className={filterTriggerClass(
                  "selection-drop text-nowrap",
                  Boolean(genderFilterValue)
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowGender((prev) => {
                    const next = !prev;
                    if (next) {
                      setSelectedGender(
                        appliedGender === "1" ||
                          appliedGender === "2" ||
                          appliedGender === "other"
                          ? appliedGender
                          : ""
                      );
                    }
                    return next;
                  });
                }}
                style={{ width: "150px", minWidth: "130px" }}
              >
                <FilterTriggerLabel label="Gender" value={genderFilterValue} />
              </div>

              {showGender && (
                <div
                  className="member-list"
                  style={{
                    width: "200px",
                    minWidth: "180px",
                    padding: "10px",
                    zIndex: 2000,
                    background: "#fff",
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", cursor: "pointer" }}>
                      <input
                        type="radio"
                        name="chat-gender-filter"
                        checked={selectedGender === ""}
                        onChange={() => setSelectedGender("")}
                      />
                      Any
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", cursor: "pointer" }}>
                      <input
                        type="radio"
                        name="chat-gender-filter"
                        checked={selectedGender === "1"}
                        onChange={() => setSelectedGender("1")}
                      />
                      Male
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", cursor: "pointer" }}>
                      <input
                        type="radio"
                        name="chat-gender-filter"
                        checked={selectedGender === "2"}
                        onChange={() => setSelectedGender("2")}
                      />
                      Female
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", cursor: "pointer" }}>
                      <input
                        type="radio"
                        name="chat-gender-filter"
                        checked={selectedGender === "other"}
                        onChange={() => setSelectedGender("other")}
                      />
                      Other
                    </label>
                  </div>

                  <div
                    className="btn-container"
                    style={{
                      display: "flex",
                      gap: "6px",
                      marginTop: "10px",
                      paddingTop: "10px",
                      borderTop: "1px solid #e0e0e0",
                    }}
                  >
                    <button
                      type="button"
                      className="btn btn-clear"
                      onClick={handleClearGender}
                      style={{
                        flex: "1",
                        padding: "4px 8px",
                        fontSize: "12px",
                        borderRadius: "4px",
                        height: "28px",
                      }}
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      className="btn btn-apply"
                      onClick={handleApplyGender}
                      style={{
                        flex: "1",
                        padding: "4px 8px",
                        fontSize: "12px",
                        borderRadius: "4px",
                        height: "28px",
                      }}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Head of Line / Color Dropdown */}
            <div ref={famRef} className="selctions-option position-relative" style={{ flexShrink: 0 }}>
              <div
                className={filterTriggerClass(
                  "selection-drop family-color-drop",
                  Boolean(familyColorFilterValue)
                )}
                style={{ fontWeight: "bold", width: "180px", minWidth: "160px", maxWidth: "200px" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFamilyColor((prev) => {
                    const next = !prev;
                    if (next) {
                      fetchFamilyColorData();
                      // Initialize selectedFamilyColor with currently applied colors
                      setSelectedFamilyColor([...appliedFamilyColor]);
                    }
                    return next;
                  });
                }}
              >
                <FilterTriggerLabel label="Line/Color" value={familyColorFilterValue} />
              </div>

              {showFamilyColor && (
                <div
                  className="member-list"
                  style={{
                    width: "300px",
                    minWidth: "200px",
                    zIndex: 2000,
                    background: "#fff"
                  }}
                >
                  {familyColorList.length === 0 ? (
                    <p>There is no family color</p>
                  ) : (
                    familyColorList.map((color, index) => {
                      const headOfLineName = formatDashboardFamilyLineHeadName(color);

                      const isChecked = selectedFamilyColor.includes(color.hex_code);

                      return (
                        <div className="member-item" key={index}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedFamilyColor([
                                    ...selectedFamilyColor,
                                    color.hex_code,
                                  ]);
                                } else {
                                  setSelectedFamilyColor(
                                    selectedFamilyColor.filter(
                                      (hex) => hex !== color.hex_code
                                    )
                                  );
                                }
                              }}
                            />
                            <div style={{ flex: "1" }}>
                              {renderNameWithColor(
                                headOfLineName || "Start/Top of Line",
                                color?.hex_code || "#000000",
                                color?.color_name
                                  ? `${color.color_name} Line`
                                  : color?.name
                                  ? `${color.name} Line`
                                  : ""
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}

                  <div
                    className="btn-container"
                    style={{
                      display: "flex",
                      gap: "8px",
                      marginTop: "10px",
                      paddingTop: "10px",
                      borderTop: "1px solid #e0e0e0",
                    }}
                  >
                    <button
                      type="button"
                      className="btn btn-clear"
                      onClick={handleClearFamilyColor}
                      style={{
                        flex: "1",
                        padding: "6px 12px",
                        fontSize: "14px",
                        borderRadius: "4px",
                      }}
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      className="btn btn-apply"
                      onClick={handleApplyFamilyColor}
                      style={{
                        flex: "1",
                        padding: "6px 12px",
                        fontSize: "14px",
                        borderRadius: "4px",
                      }}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/*Table for the family – matching screenshot 1 */}
          <div className="customer-tablle-main chat-member-table">
            <div className="table-scroll-container chat-member-table-scroll">
              <table
                className={`chat-user-table ${
                  chatMode === "group"
                    ? "chat-user-table--group"
                    : "chat-user-table--individual"
                }`}
              >
                {/* Mobile has no Email column: widths add up to 100% so the table never scrolls sideways.
                    Desktop sizes Name + Family Line to their longest entry (see App.css). */}
                {isMobileView && chatMode === "group" && (
                  <colgroup>
                    <col style={{ width: 28 }} />
                    <col style={{ width: "35%" }} />
                    <col style={{ width: "65%" }} />
                  </colgroup>
                )}
                {isMobileView && chatMode === "individual" && (
                  <colgroup>
                    <col style={{ width: "33%" }} />
                    <col style={{ width: "50%" }} />
                    <col style={{ width: "17%" }} />
                  </colgroup>
                )}
                <thead>
                  <tr>
                    {chatMode === "group" && (
                      <th style={{ padding: "4px 2px 4px 8px", width: 32, maxWidth: 32, boxSizing: "border-box" }}>
                        <input
                          type="checkbox"
                          checked={
                            filteredMembers.length > 0 &&
                            filteredMembers.every((mem) =>
                              selectedMembers.includes(mem.id)
                            )
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              // Select all filtered members
                              const newSelections = [...selectedMembers];
                              filteredMembers.forEach((mem) => {
                                if (!newSelections.includes(mem.id)) {
                                  newSelections.push(mem.id);
                                }
                              });
                              setSelectedMembers(newSelections);
                            } else {
                              // Deselect all filtered members
                              const filteredIds = filteredMembers.map((mem) => mem.id);
                              setSelectedMembers((prev) =>
                                prev.filter((id) => !filteredIds.includes(id))
                              );
                            }
                          }}
                        />
                      </th>
                    )}
                    <th 
                      className="chat-col-name"
                      onClick={() => handleSort('name')}
                      style={{
                        cursor: 'pointer',
                        userSelect: 'none',
                        padding: '4px 12px 4px 6px',
                        width: 'auto',
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <span>Name</span>
                        {sortBy === 'name' ? (
                          <span style={{ fontSize: '12px', color: '#025FEB', lineHeight: '1' }}>
                            {sortOrder === 'asc' ? '↑' : '↓'}
                          </span>
                        ) : (
                          <span style={{ fontSize: '10px', opacity: 0.5, color: '#666', lineHeight: '1', display: 'inline-block', verticalAlign: 'middle' }}>
                            <span style={{ display: 'block', lineHeight: '0.7', marginBottom: '-2px' }}>↑</span>
                            <span style={{ display: 'block', lineHeight: '0.7' }}>↓</span>
                          </span>
                        )}
                      </span>
                    </th>
                    <th 
                      className="chat-col-line"
                      onClick={() => handleSort('familyLine')}
                      style={{
                        cursor: 'pointer',
                        userSelect: 'none',
                        padding: '4px 12px 4px 6px',
                        width: 'auto',
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                        <span>Family Line/Color</span>
                        {sortBy === 'familyLine' ? (
                          <span style={{ fontSize: '12px', color: '#025FEB', lineHeight: '1' }}>
                            {sortOrder === 'asc' ? '↑' : '↓'}
                          </span>
                        ) : (
                          <span style={{ fontSize: '10px', opacity: 0.5, color: '#666', lineHeight: '1', display: 'inline-block', verticalAlign: 'middle' }}>
                            <span style={{ display: 'block', lineHeight: '0.7', marginBottom: '-2px' }}>↑</span>
                            <span style={{ display: 'block', lineHeight: '0.7' }}>↓</span>
                          </span>
                        )}
                      </span>
                    </th>
                    {/* Email: admins only; hidden on mobile – replaced by the Verify Your Email prompt */}
                    {showEmailColumn && (
                      <th className="chat-col-email" style={{ padding: '4px 12px', width: 'auto' }}>Email</th>
                    )}
                    {chatMode === "individual" && (
                      <th className="chat-col-action" style={isMobileView ? { padding: '4px 2px' } : { padding: '4px 4px', width: 36, maxWidth: 36 }}>Action</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={memberTableColSpan} className="text-center py-3">
                        <CircularProgress size={20} color="inherit" />
                      </td>
                    </tr>
                  ) : filteredMembers.length > 0 ? (
                    filteredMembers.map((mem) => (
                      <tr key={mem.id}>
                        {/* Checkbox for group chat */}
                        {chatMode === "group" && (
                          <td style={{ padding: "4px 2px 4px 8px", width: 32, maxWidth: 32, boxSizing: "border-box" }}>
                            <input
                              type="checkbox"
                              checked={selectedMembers.includes(mem.id)}
                              onChange={() => handleMemberSelection(mem.id)}
                            />
                          </td>
                        )}

                        {/* Name */}
                        <td className="chat-col-name" style={{ padding: "4px 12px 4px 6px" }}>
                          <div className="custom-table-profile">
                            <div className="">
                              <h6 className="mb-0">{formatPersonName(mem)}</h6>
                            </div>
                          </div>
                        </td>

                        {/* Family Color / Line */}
                        <td className="chat-col-line" style={{ padding: "4px 12px 4px 6px" }}>
                          {renderNameWithColor(
                            getHeadOfLineName(mem) || "Start/Top of Line",
                            mem?.hex_code || "#000000",
                            mem?.color_name ? `${mem.color_name} Line` : ""
                          )}
                        </td>

                        {/* Email – admins only, hidden on mobile */}
                        {showEmailColumn && (
                          <td className="chat-col-email" style={{ padding: '4px 12px' }}>
                            <span>
                              {mem?.email || mem?.user?.email || "-"}
                            </span>
                          </td>
                        )}

                        {/* Action - only for individual chat */}
                        {chatMode === "individual" && (
                          <td className="chat-col-action" style={isMobileView ? { padding: '2px 2px', textAlign: 'center' } : { padding: '2px 4px', width: 36, maxWidth: 36, textAlign: 'center' }}>
                            <button 
                              className="chat-init-btn"
                              onClick={() => handleCreateIndividualChat(mem)}
                              disabled={creatingChat || !currentUserId}
                              title="To start chatting choose a family member"
                              style={{
                                opacity: creatingChatForUserId === mem.user_id ? 0.6 : 1,
                                cursor: creatingChatForUserId === mem.user_id ? 'not-allowed' : 'pointer'
                              }}
                            >
                              {creatingChatForUserId === mem.user_id ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <img 
                                  src={window.getPublicUrl("/assets/images/chat-2.svg") || window.getPublicUrl("/assets/images/chat.png")} 
                                  alt="Chat"
                                  onError={(e) => {
                                    // Fallback if icon doesn't exist
                                    e.target.style.display = 'none';
                                    e.target.parentElement.innerHTML = '💬';
                                  }}
                                />
                              )}
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={memberTableColSpan}
                        className="text-center py-3 text-gray-500"
                      >
                        No members found
                      </td>
                    </tr>
                  )}
                </tbody>
            </table>
            </div>
          </div>
        </div>
      </div>

      {/*Button when the chatMode === group */}
      {chatMode && chatMode === "group" && (
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "20px", paddingTop: "20px", borderTop: "1px solid #e8e8e8" }}>
          <button 
            onClick={() => onClose(true)}
            style={{
              padding: "10px 24px",
              fontSize: "14px",
              fontWeight: "500",
              color: "#666",
              backgroundColor: "#fff",
              border: "1px solid #D9D9D9",
              borderRadius: "4px",
              cursor: "pointer",
              transition: "all 0.2s"
            }}
            onMouseOver={(e) => { e.target.style.backgroundColor = "#f5f5f5"; }}
            onMouseOut={(e) => { e.target.style.backgroundColor = "#fff"; }}
          >
            Cancel
          </button>
          <button
            onClick={editGroupChat ? handleUpdateGroupChat : handleCreateGroupChat}
            disabled={creatingChat || !currentUserId || (!editGroupChat && selectedMembers.length === 0) || (!editGroupChat && !groupName.trim())}
            style={{
              padding: "10px 24px",
              fontSize: "14px",
              fontWeight: "500",
              color: "#fff",
              backgroundColor: creatingChat ? "#6c9eff" : "#025FEB",
              border: "1px solid #025FEB",
              borderRadius: "4px",
              cursor: creatingChat ? "not-allowed" : "pointer",
              transition: "all 0.2s",
              display: "flex",
              alignItems: "center",
              gap: "8px"
            }}
            onMouseOver={(e) => {
              if (!creatingChat) {
                e.target.style.backgroundColor = "#0250d0";
              }
            }}
            onMouseOut={(e) => {
              if (!creatingChat) {
                e.target.style.backgroundColor = "#025FEB";
              }
            }}
          >
            {creatingChat && <CircularProgress size={16} color="inherit" />}
            {creatingChat ? (editGroupChat ? "Updating..." : "Creating...") : (editGroupChat ? "Update Group" : "Create Group")}
          </button>
        </div>
      )}

      {/* Mobile: Email column is hidden, so offer to request verification from the admin */}
      {isMobileView && showVerifyEmail && (
        <div
          className="verify-email-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="verify-email-title"
        >
          <div className="verify-email-modal">
            <button
              type="button"
              className="verify-email-close"
              onClick={() => setShowVerifyEmail(false)}
              aria-label="Close"
            >
              ×
            </button>
            <h3 className="verify-email-title" id="verify-email-title">
              Verify Your Email
            </h3>
            <p className="verify-email-text">
              Tap Verify Email to send a request to your family admin. If your
              email is already verified, tap Already Verified and we won't ask
              again.
            </p>
            <div className="verify-email-actions">
              <button
                type="button"
                className="verify-email-btn verify-email-btn--secondary"
                onClick={handleAlreadyVerified}
                disabled={sendingVerifyRequest}
              >
                Already Verified
              </button>
              <button
                type="button"
                className="verify-email-btn"
                onClick={handleSendVerifyEmailRequest}
                disabled={sendingVerifyRequest}
              >
                {sendingVerifyRequest && (
                  <CircularProgress size={16} color="inherit" />
                )}
                {sendingVerifyRequest ? "Sending..." : "Verify Email"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatModal;
